import React from 'react';

import dayjs from '~/timeInit';

import CheckIcon from '@mui/icons-material/Check';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Box, Button, InputAdornment, Stack, TextField, Tooltip, Typography } from '@mui/material';

import { CM_CHECKLIST_CATEGORIES } from './constants';
import type { ChecklistCategoryId, ChecklistTabId } from './types';

interface Props {
  activeTabId: ChecklistTabId;
  areVisibleQueueItemsExpanded: boolean;
  categoryCounts: Record<ChecklistCategoryId, number>;
  isApprovalQueueMode: boolean;
  needsApprovalCount: number;
  searchMatchCounts?: Record<ChecklistCategoryId, number>;
  searchQuery: string;
  showAutoApproved: boolean;
  syncedAt?: string;
  visibleQueueItemCount: number;
  onExpandVisibleCards: () => void;
  onMarkAllApproved?: () => void;
  onMarkAllApprovedDisabled?: boolean;
  onSearchQueryChange: (searchQuery: string) => void;
  onShowAutoApprovedChange: () => void;
  onTabChange: (tabId: ChecklistTabId) => void;
}

export default function ChecklistToolbar({
  activeTabId,
  areVisibleQueueItemsExpanded,
  categoryCounts,
  isApprovalQueueMode,
  needsApprovalCount,
  searchMatchCounts,
  searchQuery,
  showAutoApproved,
  syncedAt,
  visibleQueueItemCount,
  onExpandVisibleCards,
  onMarkAllApproved,
  onMarkAllApprovedDisabled,
  onSearchQueryChange,
  onShowAutoApprovedChange,
  onTabChange,
}: Props) {
  const tabs = [
    { id: 'approvalQueue' as const, label: `Requires approval (${needsApprovalCount})` },
    ...CM_CHECKLIST_CATEGORIES.map((category) => {
      const matchCount = searchMatchCounts?.[category.id];

      if (matchCount !== undefined) {
        return {
          id: category.id,
          label: `${category.label} (${matchCount} match${matchCount === 1 ? '' : 'es'})`,
        };
      }

      const count = categoryCounts[category.id];

      return {
        id: category.id,
        label: count > 0 ? `${category.label} (${count})` : category.label,
      };
    }),
  ];

  return (
    <Stack spacing={1.5} alignItems="stretch">
      <Box
        sx={{
          bgcolor: 'grey.100',
          borderRadius: 1.25,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          p: 1,
          width: '100%',
        }}
      >
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;

          return (
            <Button
              key={tab.id}
              color="inherit"
              size="small"
              variant={isActive ? 'contained' : 'outlined'}
              onClick={() => onTabChange(tab.id)}
              sx={{
                bgcolor: isActive ? 'background.paper' : 'transparent',
                borderColor: isActive ? 'primary.main' : 'divider',
                borderRadius: 1.5,
                boxShadow: isActive ? 2 : 'none',
                color: isActive ? 'text.primary' : 'text.secondary',
                fontWeight: isActive ? 700 : 500,
                textTransform: 'none',
              }}
            >
              {tab.label}
            </Button>
          );
        })}
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        <TextField
          onChange={(event) => onSearchQueryChange(event.target.value)}
          placeholder="Search..."
          size="small"
          value={searchQuery}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 220 }}
        />
        <Button
          disabled={visibleQueueItemCount === 0}
          size="small"
          variant="outlined"
          startIcon={areVisibleQueueItemsExpanded ? <ExpandLessIcon /> : <UnfoldMoreIcon />}
          onClick={onExpandVisibleCards}
          sx={{ textTransform: 'none' }}
        >
          {areVisibleQueueItemsExpanded ? 'Collapse all' : 'Expand all'}
        </Button>
        {!isApprovalQueueMode && (
          <Button
            size="small"
            variant="outlined"
            startIcon={showAutoApproved ? <VisibilityOffIcon /> : <VisibilityIcon />}
            onClick={onShowAutoApprovedChange}
            sx={{ textTransform: 'none' }}
          >
            {showAutoApproved ? 'Hide approved' : 'Show approved'}
          </Button>
        )}
        {!isApprovalQueueMode && syncedAt && (
          <Tooltip title={dayjs(syncedAt).format('lll')}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
              <SyncIcon fontSize="small" />
              <Typography variant="caption">Last modified {dayjs(syncedAt).fromNow()}</Typography>
            </Stack>
          </Tooltip>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {!isApprovalQueueMode && onMarkAllApproved && (
          <Button
            disabled={onMarkAllApprovedDisabled}
            size="small"
            startIcon={<CheckIcon />}
            variant="contained"
            onClick={onMarkAllApproved}
            sx={{ textTransform: 'none' }}
          >
            Mark all approved
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
